import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';

const PartnerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user, role, isLoading, signIn } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in as partner
  useEffect(() => {
    if (!isLoading && user && role === 'partner') {
      navigate('/partner-dashboard');
    }
  }, [user, role, isLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing Credentials",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        toast({
          title: "Login Failed",
          description: error.message || "Invalid email or password.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Check role after login - the useEffect will handle redirect
      toast({
        title: "Login Successful",
        description: "Redirecting to your dashboard...",
      });
    } catch (error: any) {
      toast({
        title: "Login Failed",
        description: error.message || "An error occurred during login.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-[30%_70%] gap-8 items-start">
            <div>
              <Card className="border border-delphi-gray/20 bg-delphi-dark/80 backdrop-blur-sm shadow-lg">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl text-delphi-light">Partner Login</CardTitle>
                  <CardDescription className="text-delphi-light/70">
                    Enter your credentials to access your dashboard
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-delphi-light">
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="password" className="text-sm font-medium text-delphi-light">
                        Password
                      </label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                        disabled={isSubmitting}
                      />
                    </div>
                  </form>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full bg-delphi-red hover:bg-red-600"
                    onClick={handleLogin}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        Sign In
                        <ArrowRight size={16} className="ml-2" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
              <p className="text-center mt-4 text-delphi-light/60 text-sm">
                Forgot your login details? <a href="/contact" className="text-delphi-red hover:underline">Contact Support</a>
              </p>
              <div className="mt-4">
                <a href="/partner-request">
                  <Button 
                    variant="outline" 
                    className="w-full border-delphi-red text-delphi-red hover:bg-delphi-red hover:text-white"
                  >
                    Become a Partner – Request Access
                  </Button>
                </a>
              </div>
              <div className="mt-4">
                <a href="/manager-login" className="block">
                  <Button 
                    variant="ghost" 
                    className="w-full py-3 text-base border border-delphi-gray/40 text-delphi-light/80 hover:bg-delphi-gray/20 hover:text-delphi-light hover:border-delphi-red/50 transition-all"
                  >
                    Manager Login
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-delphi-red/20 via-delphi-red/10 to-transparent backdrop-blur-sm border border-delphi-red/20 p-1">
              <div className="bg-delphi-dark/40 backdrop-blur-sm p-6 rounded-xl border border-delphi-gray/20 shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-delphi-light mb-2">Partner Dashboard Preview</h3>
                  <p className="text-delphi-light/70">
                    Access your personalized investment dashboard with real-time portfolio tracking, performance metrics, and exclusive reports.
                  </p>
                </div>

                <div className="bg-delphi-dark/80 p-6 rounded-lg border border-delphi-gray/20 mb-6">
                  <h4 className="text-lg font-semibold text-delphi-light mb-4">Sample Portfolio Performance</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={[
                          { month: 'Jan', value: 2000000 },
                          { month: 'Feb', value: 1950000 },
                          { month: 'Mar', value: 2200000 },
                          { month: 'Apr', value: 2150000 },
                          { month: 'May', value: 2400000 },
                          { month: 'Jun', value: 2350000 },
                          { month: 'Jul', value: 2600000 },
                          { month: 'Aug', value: 2800000 },
                          { month: 'Sep', value: 2750000 },
                          { month: 'Oct', value: 3000000 },
                          { month: 'Nov', value: 2900000 },
                          { month: 'Dec', value: 3200000 }
                        ]}
                        margin={{ top: 5, right: 20, bottom: 5, left: 20 }}
                      >
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#ea384c" 
                          dot={false} 
                          strokeWidth={2}
                          activeDot={{ r: 4, stroke: "#ea384c", strokeWidth: 2, fill: "#fff" }}
                        />
                        <XAxis 
                          dataKey="month" 
                          stroke="#666" 
                          tick={{ fill: '#999' }}
                        />
                        <YAxis
                          stroke="#666"
                          tick={{ fill: '#999' }}
                          tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1a1a1a', border: '1px solid #333' }}
                          labelStyle={{ color: '#fff' }}
                          formatter={(value: number) => [`$${(value / 1000000).toFixed(2)}M`, 'Portfolio Value']}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="text-center mt-4 bg-delphi-dark/50 p-3 rounded border border-delphi-gray/20">
                  <p className="text-delphi-light/80 text-sm italic">
                    Login to access your actual dashboard with real-time data, portfolio analytics, and investment reports.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PageLayout>
  );
};

export default PartnerLogin;
